<template>
  <v-container>
    <div>
      <v-data-table
        :headers="headers"
        :items="items"
        :height="items.length > 10 ? 480 : undefined"
        :fixed-header="items.length > 10"
        :footer-props="{ itemsPerPageText: '行/ページ:' }"
        no-data-text="データがありません"
      >
        <template #top>
          <v-toolbar
            flat
            color="transparent"
          >
            <v-spacer />
            <v-btn
              color="primary"
              :ripple="false"
              small
              @click="openCreateDialog"
            >
              + 計測項目追加
            </v-btn>
          </v-toolbar>
        </template>

        <template #[`item.attributes.itemType`]="{ item }">
          {{ itemTypeLabelMap[item.attributes.itemType] || item.attributes.itemType }}
        </template>

        <template #[`item.detail`]="{ item }">
          <template v-if="item.attributes.itemType === 'context_constant'">
            {{ contextConstantLabel(item.attributes.contextConstantKey) }}
          </template>
          <template v-else-if="item.attributes.itemType === 'business_rule_result'">
            {{ item.attributes.businessRuleName }}
          </template>
          <template v-else-if="item.attributes.itemType === 'ratio'">
            {{ formulaItemLabel(item.attributes.numeratorItemId) }} / {{ formulaItemLabel(item.attributes.denominatorItemId) }}
          </template>
        </template>

        <template #[`item.attributes.metricNames`]="{ item }">
          <v-chip
            v-for="name in item.attributes.metricNames"
            :key="name"
            x-small
            class="mr-1"
          >
            {{ name }}
          </v-chip>
        </template>

        <template #[`item.actions`]="{ item }">
          <v-btn
            icon
            :ripple="false"
            @click="openEditDialog(item)"
          >
            <v-icon small>
              {{ icons.mdiPencilOutline }}
            </v-icon>
          </v-btn>
          <v-dialog max-width="400">
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                :ripple="false"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon small>
                  {{ icons.mdiTrashCanOutline }}
                </v-icon>
              </v-btn>
            </template>
            <template #default="dialog">
              <v-card>
                <v-card-title>削除の確認</v-card-title>
                <v-card-text>
                  この計測項目を削除しますか？この操作は取り消せません。
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    text
                    small
                    @click="dialog.value = false"
                  >
                    キャンセル
                  </v-btn>
                  <v-btn
                    color="error"
                    small
                    @click="destroyItem(item.id); dialog.value = false"
                  >
                    削除
                  </v-btn>
                </v-card-actions>
              </v-card>
            </template>
          </v-dialog>
        </template>
      </v-data-table>
    </div>

    <v-dialog
      v-model="editDialog"
      max-width="500"
      @click:outside="resetParams"
    >
      <template #default="dialog">
        <v-card>
          <v-card-title>{{ isNew ? '新規作成' : '編集' }}</v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="params.label"
                  label="名前"
                  autofocus
                />
              </v-col>
              <v-col cols="12">
                <v-select
                  v-model="params.itemType"
                  :items="itemTypeOptions"
                  label="種別"
                />
              </v-col>

              <!-- context_constant -->
              <template v-if="params.itemType === 'context_constant'">
                <v-col cols="12">
                  <v-select
                    v-model="params.contextConstantKey"
                    :items="contextConstants"
                    label="定数キー"
                    item-text="label"
                    item-value="key"
                  />
                </v-col>
              </template>

              <!-- business_rule_result -->
              <template v-if="params.itemType === 'business_rule_result'">
                <v-col cols="12">
                  <v-select
                    :value="selectedBusinessRuleKey"
                    :items="groupedBusinessRules"
                    item-text="name"
                    item-value="_listKey"
                    label="ビジネスルール"
                    @change="onBusinessRuleChange"
                  >
                    <template #item="{ item }">
                      <v-list-item-content
                        v-if="item._isNameHeader"
                        class="pl-4"
                      >
                        <v-list-item-title class="text-caption grey--text">
                          {{ item.name }} ({{ item._count }})
                        </v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-content
                        v-else-if="!item.header"
                        class="pl-8"
                      >
                        <v-list-item-title>{{ item.name }}</v-list-item-title>
                      </v-list-item-content>
                    </template>
                  </v-select>
                </v-col>
              </template>

              <!-- ratio -->
              <template v-if="params.itemType === 'ratio'">
                <v-col cols="12">
                  <v-select
                    v-model="params.numeratorItemId"
                    :items="formulaItems"
                    label="分子（計測項目）"
                    item-text="label"
                    item-value="id"
                  />
                </v-col>
                <v-col cols="12">
                  <v-select
                    v-model="params.denominatorItemId"
                    :items="formulaItems"
                    label="分母（計測項目）"
                    item-text="label"
                    item-value="id"
                  />
                </v-col>
              </template>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              text
              small
              @click="closeDialog(dialog)"
            >
              キャンセル
            </v-btn>
            <v-btn
              color="primary"
              small
              :disabled="!params.label || !params.itemType"
              @click="saveItem(dialog)"
            >
              {{ isNew ? '作成' : '更新' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </template>
    </v-dialog>
  </v-container>
</template>

<script>
import {
  ref,
  reactive,
  computed,
  onMounted,
} from '@vue/composition-api'
import { mdiPencilOutline, mdiTrashCanOutline } from '@mdi/js'
import ReportFormulaItemApi from '@/api/v1/admin/ReportFormulaItem'

export default {
  setup() {
    const items = ref([])
    const businessRules = ref([])
    const contextConstants = ref([])
    const formulaItems = ref([])
    const loading = ref(false)
    const editDialog = ref(false)
    const params = reactive({
      id: null,
      label: '',
      itemType: null,
      contextConstantKey: null,
      businessRuleId: null,
      numeratorItemId: null,
      denominatorItemId: null,
    })

    const isNew = computed(() => params.id === null)

    const groupedBusinessRules = computed(() => {
      const typeGroups = {}
      businessRules.value.forEach(br => {
        const typeKey = br.aggregationLabel || 'その他'
        const nameKey = br.aggregationName || '不明'
        if (!typeGroups[typeKey]) typeGroups[typeKey] = {}
        if (!typeGroups[typeKey][nameKey]) typeGroups[typeKey][nameKey] = []
        typeGroups[typeKey][nameKey].push(br)
      })

      return Object.entries(typeGroups).flatMap(([typeLabel, nameGroups]) => [
        { header: typeLabel },
        ...Object.entries(nameGroups).flatMap(([nameLabel, rules]) => [
          {
            name: nameLabel, _isNameHeader: true, disabled: true, id: `__h_${typeLabel}_${nameLabel}`, _listKey: `__h_${typeLabel}_${nameLabel}`, _count: rules.length,
          },
          ...rules.map(br => ({ ...br, _listKey: `${br.id}_${typeLabel}_${nameLabel}` })),
        ]),
      ])
    })

    const selectedBusinessRuleKey = computed(() => {
      if (!params.businessRuleId) return null
      const found = groupedBusinessRules.value.find(
        item => !item._isNameHeader && !item.header && item.id === params.businessRuleId,
      )

      return found ? found._listKey : null
    })

    const onBusinessRuleChange = val => {
      const found = groupedBusinessRules.value.find(item => item._listKey === val)
      params.businessRuleId = found ? found.id : null
    }

    const itemTypeOptions = [
      { text: 'コンテキスト定数', value: 'context_constant' },
      { text: 'ビジネスルール結果', value: 'business_rule_result' },
      { text: '比率', value: 'ratio' },
    ]

    const itemTypeLabelMap = Object.fromEntries(itemTypeOptions.map(o => [o.value, o.text]))

    const contextConstantLabel = key => {
      const found = contextConstants.value.find(c => c.key === key)

      return found ? found.label : key
    }

    const formulaItemLabel = id => {
      const found = formulaItems.value.find(f => String(f.id) === String(id))

      return found ? found.label : id
    }

    const headers = [
      { text: '名前', value: 'attributes.label' },
      { text: '種別', value: 'attributes.itemType', sortable: false },
      { text: '詳細', value: 'detail', sortable: false },
      { text: '指標', value: 'attributes.metricNames', sortable: false },
      {
        text: '', value: 'actions', sortable: false, align: 'end',
      },
    ]

    const fetchItems = async () => {
      loading.value = true
      try {
        const res = await ReportFormulaItemApi.getList()
        items.value = res.data.reportFormulaItems?.data || []
        businessRules.value = res.data.meta?.businessRules || []
        contextConstants.value = res.data.meta?.contextConstants || []
        formulaItems.value = res.data.meta?.formulaItems || []
      } catch (e) {
        // TODO
      } finally {
        loading.value = false
      }
    }

    const resetParams = () => {
      params.id = null
      params.label = ''
      params.itemType = null
      params.contextConstantKey = null
      params.businessRuleId = null
      params.numeratorItemId = null
      params.denominatorItemId = null
    }

    const openCreateDialog = () => {
      resetParams()
      editDialog.value = true
    }

    const openEditDialog = item => {
      params.id = item.id
      params.label = item.attributes.label
      params.itemType = item.attributes.itemType
      params.contextConstantKey = item.attributes.contextConstantKey || null
      params.businessRuleId = item.attributes.businessRuleId || null
      params.numeratorItemId = item.attributes.numeratorItemId || null
      params.denominatorItemId = item.attributes.denominatorItemId || null
      editDialog.value = true
    }

    const closeDialog = dialog => {
      // eslint-disable-next-line no-param-reassign
      dialog.value = false
      resetParams()
    }

    const saveItem = async dialog => {
      try {
        const payload = {
          label: params.label,
          item_type: params.itemType,
          context_constant_key: params.contextConstantKey,
          business_rule_id: params.businessRuleId,
          numerator_item_id: params.numeratorItemId,
          denominator_item_id: params.denominatorItemId,
        }
        if (isNew.value) {
          await ReportFormulaItemApi.create(payload)
        } else {
          await ReportFormulaItemApi.update(params.id, payload)
        }
        closeDialog(dialog)
        fetchItems()
      } catch (e) {
        // TODO
      }
    }

    const destroyItem = async id => {
      try {
        await ReportFormulaItemApi.destroy(id)
        await fetchItems()
      } catch (e) {
        // TODO
      }
    }

    onMounted(fetchItems)

    return {
      items,
      businessRules,
      groupedBusinessRules,
      selectedBusinessRuleKey,
      onBusinessRuleChange,
      contextConstants,
      formulaItems,
      loading,
      editDialog,
      params,
      isNew,
      headers,
      itemTypeOptions,
      itemTypeLabelMap,
      contextConstantLabel,
      formulaItemLabel,
      openCreateDialog,
      openEditDialog,
      closeDialog,
      saveItem,
      destroyItem,
      resetParams,
      icons: {
        mdiPencilOutline,
        mdiTrashCanOutline,
      },
    }
  },
}
</script>
